import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseFormComponent } from "../base-form/base-form.component";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbDialogService,
  NbInputModule,
  NbOptionModule,
  NbSelectModule,
  NbSpinnerModule
} from "@nebular/theme";
import { AsyncPipe, NgClass, NgStyle } from "@angular/common";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { KPI } from "../../../../@core/api";
import { map, Subscription, switchMap, tap } from "rxjs";
import { I18nService, PriceSheetService, QuantityUnitsService, SaveToastService, ScopeService } from "../../../../@core/utils";
import { idMapToArray, toLoadingState } from "../../../../@core/utils/scope.service";

@Component({
  selector: "app-price-sheet-form",
  templateUrl: "./price-sheet-form.component.html",
  styleUrls: ["../base-form/base-form.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NgClass,
    NbSpinnerModule,
    NbButtonModule,
    FontAwesomeModule,
    NbInputModule,
    TranslateModule,
    NbSelectModule,
    NbOptionModule,
    NgStyle,
    AsyncPipe
  ]
})
export class PriceSheetFormComponent extends BaseFormComponent implements OnInit, OnDestroy {
  @Input() assetType: KPI.AssetType;
  @Input() priceSheet: KPI.PriceSheet;
  @Input() fromDetail: boolean = false;
  @Input() isDefault: boolean = false;
  public quantityUnit: KPI.QuantityUnit = null;
  public budgetScopes$ = this.scopeService.budgetScopes$.pipe(
    idMapToArray(),
    map(type => type.sort((a, b) => a.order - b.order)),
    tap(scopes => {
      const fourniPoseScope = scopes.find(scope => scope.is_fourni_pose);
      if (fourniPoseScope && !this.priceSheet) {
        this.form.controls["budget_scope"].setValue(fourniPoseScope.id);
        this.initialValues.budget_scope = fourniPoseScope.id;
      }
    }),
    toLoadingState<KPI.BudgetScope[]>()
  );
  public investmentTypes$ = this.scopeService.investmentTypes$.pipe(
    idMapToArray(),
    map(types => types.filter(t => t.replacement)),
    map(types => types.sort((a, b) => (a.ordering || 0) - (b.ordering || 0))),
    tap(types => {
      if (!this.priceSheet && types.length) {
        this.form?.controls["investment_type"]?.setValue(types[0].id);
        this.initialValues.investment_type = types[0].id;
      }
    })
  );
  protected subscriptions: Subscription[];

  constructor(
    protected nbDialogRef: NbDialogRef<BaseFormComponent>,
    protected dialogService: NbDialogService,
    protected translate: TranslateService,
    private scopeService: ScopeService,
    private priceSheetService: PriceSheetService,
    private quantityUnitService: QuantityUnitsService,
    private i18n: I18nService,
    private saveToast: SaveToastService
  ) {
    super(nbDialogRef, dialogService, translate);
  }

  public title = this.translate.instant("Price sheet title");
  public currency: string = this.i18n.getCurrency();

  ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(
      this.quantityUnitService.quantityUnits$.subscribe(quantityUnits => {
        if (this.priceSheet) {
          this.quantityUnit = quantityUnits.find(unit => unit.id === this.priceSheet.quantity_unit?.id);
        } else if (this.assetType) {
          this.quantityUnit = quantityUnits.find(unit => unit.id === this.assetType.quantity_unit);
        }
        this.initForm();
      })
    );
    this.initializeBackdropClick();
  }

  public onSubmit(): void {
    this.loading = true;

    this.scopeService.user$
      .pipe(
        switchMap(user => {
          if (!user) {
            this.loading = false;
            return [];
          }

          if (this.priceSheet) {
            return this.priceSheetService.updatePriceSheet(
              this.priceSheet.id,
              this.form.value,
              user.id,
              this.priceSheet.is_default
            );
          } else {
            return this.priceSheetService.createPriceSheet(
              this.assetType.id,
              this.assetType.quantity_unit,
              this.form.value,
              user.id,
              this.isDefault
            );
          }
        })
      )
      .subscribe({
        next: () => {
          this.loading = false;
          if (this.fromDetail) {
            this.priceSheetService.clearPriceSheetCache(this.priceSheet.id);
          }
          this.priceSheetService.pricesheetsHaveChanged();
          this.close();
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  public anyFieldValueChanged(): boolean {
    const anyFieldChanged = Object.keys(this.initialValues).some((key: string) => this.fieldValueChanged(key));
    return anyFieldChanged;
  }

  public goToAudit(): void {}

  public isFormValid(): boolean {
    return this.form.valid;
  }

  protected setUpAvailableValues(): void {}

  private initForm() {
    this.initialValues = {
      label: this.priceSheet?.label || "",
      asset_type_name: this.assetType?.name || "",
      average_price: this.priceSheet ? Math.round(this.priceSheet.average_price) : null,
      min_price: this.priceSheet?.min_price ? Math.round(this.priceSheet.min_price) : null,
      max_price: this.priceSheet?.max_price ? Math.round(this.priceSheet.max_price) : null,
      quantity: this.priceSheet ? Math.round(this.priceSheet.quantity) : 1,
      quantity_unit: this.quantityUnit?.name || null,
      investment_type: this.priceSheet?.investment_type?.id || null,
      budget_scope: this.priceSheet?.budget_scope?.id || null,
      comments: this.priceSheet?.comments || ""
    };

    this.form = new UntypedFormGroup({
      label: new UntypedFormControl(this.initialValues.label, [Validators.required, Validators.maxLength(255)]),
      asset_type_name: new UntypedFormControl({ value: this.initialValues.asset_type_name, disabled: true }, [
        Validators.required
      ]),
      average_price: new UntypedFormControl(this.initialValues.average_price, [Validators.required]),
      min_price: new UntypedFormControl(this.initialValues.min_price),
      max_price: new UntypedFormControl(this.initialValues.max_price),
      quantity: new UntypedFormControl(this.initialValues.quantity, [Validators.required, Validators.min(1)]),
      quantity_unit: new UntypedFormControl(this.initialValues.quantity_unit),
      investment_type: new UntypedFormControl(this.initialValues.investment_type),
      budget_scope: new UntypedFormControl(this.initialValues.budget_scope, [Validators.required]),
      comments: new UntypedFormControl(this.initialValues.comments)
    });
  }

  public autoSave(field?: string): void {
    if (!this.priceSheet) {
      return;
    }
    if (field && !this.fieldValueChanged(field)) {
      return;
    }
    if (this.isFormValid() && !this.loading) {
      this.saveToast.showSaving();
      this.scopeService.user$
        .pipe(
          switchMap(user => {
            if (!user) {
              return [];
            }
            return this.priceSheetService.updatePriceSheet(
              this.priceSheet.id,
              this.form.value,
              user.id,
              this.priceSheet.is_default
            );
          })
        )
        .subscribe({
          next: () => {
            this.initialValues = { ...this.initialValues, ...this.form.value };
            if (this.fromDetail) {
              this.priceSheetService.clearPriceSheetCache(this.priceSheet.id);
            }
            this.priceSheetService.pricesheetsHaveChanged();
            this.saveToast.showSaved();
          },
          error: () => {
            this.saveToast.close();
          }
        });
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
